<script>
    export let data;
    export let data_errors;
    export let scale;
    export let margin;
    export let unit;
    export let rec_limit;
    export let source;
    export let scales;
    export let y_domain;
    export let selected_survey_cycle;

    import GraphPointsGroup from './GraphPointsGroup.svelte';
    import Modal from './Modal.svelte';
    import { icon_folder } from '../inputs/data_paths';
    import constants from '../utils/constants';

    import { scaleBand, scaleLinear } from 'd3-scale';
    import _ from 'lodash';

    const axis_font_size = 12;

    // in this file the static axis is the x-axis and the dynamic axis is the y-axis
    let width = 0, height = 0;
    let prairie_data, canada_data;
    let x_scale, x_domain, y_scale;
    let graph_center;
    let lod, lod_path, lod_top;
    let rec_limit_path, rec_limit_bottom;
    $: {
        canada_data = data["Canada"];
        prairie_data = data["Prairies"];
        let total_data = _.concat(canada_data, prairie_data);

        // get limit of detection
        let lods = _.map(canada_data, 'lod');
        lod = canada_data.length ? _.min(lods) : null;

        x_domain = _(total_data).map('static').uniq().value();
        x_scale = scaleBand()
            .domain(x_domain)
            .range([margin.left, width - margin.right])
            .paddingInner(1)
            .paddingOuter(0.5);
        y_scale = scaleLinear().domain(y_domain).range([height - margin.top, margin.bottom]);

        graph_center = margin.left + (width - margin.left - margin.right) / 2;

        // make sure lod height is at least *min_height*
        let min_height = 20;
        let lod_height = !_.isNaN(lod) && !_.isNil(lod) ? height - margin.top - y_scale(lod): null;
        lod_top = (_.isNaN(lod) || _.isNil(lod) || _.max(lods) < y_domain[0]
            ? null 
            : (!_.isNil(lod_height) && lod_height < min_height ?  height - margin.top + (min_height - lod_height) : height - margin.top));
        lod_path = (_.isNaN(lod) || _.isNil(lod) || _.max(lods) < y_domain[0]
            ? null 
            : `M ${margin.left} ${lod_top} ${_.map(lods, (lod) => {
                return `V ${_.min([height - margin.top, y_scale(lod)])} h ${x_scale.step()}`;
            }).join(' ')} V ${lod_top}`
        );

        // make sure rec_limit width is at least *min_height*
        let rec_limit_height = y_scale(rec_limit) - margin.bottom;
        rec_limit_bottom = (isNaN(rec_limit) || rec_limit > y_domain[1]
            ? null 
            : margin.bottom - (rec_limit_height < min_height ? min_height - rec_limit_height : 0));
        rec_limit_path = (isNaN(rec_limit) || rec_limit > y_domain[1]
            ? null 
            : `M ${margin.left} ${rec_limit_bottom} V ${y_scale(rec_limit)} h ${width - margin.left - margin.right} V ${rec_limit_bottom}`
        );

        if(info_error_modal) info_error_modal.hide();
    }

    let chevron_height = 10;
    const chevronClick = () => {
        scale = _.find(scales, (scale_option) => {
            return scale_option.key !== scale;
        }).key;
    };
    let info_errors_to_show = {};
    let info_error_modal = null;
    const verticalGraphShowError = (errors) => {
        info_errors_to_show = errors;
        info_error_modal.show();
    };
</script>

<div class="axis-label">{unit}</div>
<div class="vertical-graph__graph" bind:clientWidth={width} bind:clientHeight={height}>
    <img 
        id="vertical-graph__scale" 
        src={`${icon_folder}images/chevron-${scale === 'data' ? 'up' : 'down'}-icon.svg`} 
        alt={`chevron`}
        height={chevron_height}
        style={`top:${13}px;left:${-chevron_height - 5}px`}
        on:click={chevronClick}
    />
    <svg {height} {width}>

        <pattern id="prairiesHorizontalDiagonalHatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(150 0 0)">
            <rect x="0" y="0" width="6" height="3" fill="rgba(119,184,0,0.3)"></rect>
            <rect x="0" y="3" width="6" height="3" fill="rgba(119,184,0,0.5)"></rect>
        </pattern>
        <pattern id="canadaHorizontalDiagonalHatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(150 0 0)">
            <rect x="0" y="0" width="6" height="3" fill="rgba(0,170,210,0.2)"></rect>
            <rect x="0" y="3" width="6" height="3" fill="rgba(0,170,210,0.4)"></rect>
        </pattern>
        <pattern id="prairiesVerticalDiagonalHatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(120 0 0)">
            <rect x="0" y="0" width="6" height="3" fill="rgba(119,184,0,0.3)"></rect>
            <rect x="0" y="3" width="6" height="3" fill="rgba(119,184,0,0.5)"></rect>
        </pattern>
        <pattern id="canadaVerticalDiagonalHatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(120 0 0)">
            <rect x="0" y="0" width="6" height="3" fill="rgba(0,170,210,0.2)"></rect>
            <rect x="0" y="3" width="6" height="3" fill="rgba(0,170,210,0.4)"></rect>
        </pattern>
        {#each x_domain as static_val}
            <g transform={`translate(${x_scale(static_val)} ${height - 20})`}>
                <text 
                    class={`axis-label${selected_survey_cycle === static_val ? ' selected' : ''}`} 
                    text-anchor="middle"
                >{static_val}</text>
                {#if data_errors[static_val].missing_data_strings.length > 0 || data_errors[static_val].unreliable_data_strings.length > 0}
                    <image 
                        class="vertical-graph__caution-icon"
                        transform={`translate(${static_val === '' ? 0 : 12} -12)`} 
                        href={`${icon_folder}/images/${data_errors[static_val].missing_data_strings.length > 0 ? 'grey' : 'yellow'}-caution-icon.svg`}
                        on:click={() => {verticalGraphShowError(data_errors[static_val])}}
                    ></image>
                {/if}
            </g>
        {/each}
        <g transform={`translate(${graph_center} ${height})`}>
            <text class="axis-label vertical-graph__bottom-axis-label" text-anchor="middle">Cycle</text>
        </g>
        {#each y_scale.ticks(constants.num_axis_ticks) as tick}
            <g transform={`translate(0 ${y_scale(tick)})`}>
                <text class="axis-label" alignment-baseline="middle">{tick}</text>
                <line class="axis-reference" x1={margin.left} x2={width - margin.right} y1=0 y2=0 />
            </g>
        {/each}
        {#if lod_path}
            <path 
                class="lod-path"
                d={lod_path}
            />
        {/if}
        {#if rec_limit_path}
            <path 
                class="rec-limit-path"
                d={rec_limit_path}
            />
        {/if}
        {#if !_.isEmpty(prairie_data)}
            <g transform={_.isEmpty(canada_data) ? '' : `translate(8 0)`}>
                <GraphPointsGroup
                    static_scale={x_scale}
                    dynamic_scale={y_scale}
                    horizontal={false}
                    data={prairie_data}
                    region="prairies"
                />
            </g>
        {/if}
        {#if !_.isEmpty(canada_data)}
            <g>
                <GraphPointsGroup
                    static_scale={x_scale}
                    dynamic_scale={y_scale}
                    horizontal={false}
                    data={canada_data}
                    region="canada"
                />
            </g>
        {/if}
        {#if lod_path}
            <text 
                class="axis-label"
                text-anchor="middle"
                transform={`translate(${graph_center} ${lod_top - 5})`}
            >{`limit of detection`}</text>
        {/if}
        {#if rec_limit_path}
            <text 
                class="axis-label"
                alignment-baseline="hanging"
                text-anchor="middle"
                transform={`translate(${graph_center} ${rec_limit_bottom + 10})`}
            >{`biomonitoring guidance value: ${rec_limit}${unit}`}</text>
        {/if}
        {#if source}
            <text 
                class="axis-label"
                alignment-baseline="hanging"
                text-anchor="middle"
                transform={`translate(${graph_center} ${rec_limit_bottom + 10 + axis_font_size})`}
            >{`(${source})`}</text>
        {/if}
    </svg>
    <Modal
        background_dark={false}
        whole_screen={false}
        bind:this={info_error_modal}
    >
        <div id="vertical-graph__info-error-modal">
            {#if _.get(info_errors_to_show, 'missing_data_strings.length', 0) > 0}
                <div class="vertical-graph__info-error-modal-header">
                    Missing Data
                </div>
                {#each info_errors_to_show.missing_data_strings as error_string}
                    <div class="vertical-graph__info-error-modal-content">
                        {error_string}
                    </div>
                {/each}
            {/if}
            {#if _.get(info_errors_to_show, 'unreliable_data_strings.length', 0) > 0}
                <div class="vertical-graph__info-error-modal-header">
                    Unreliable Data
                </div>
                {#each info_errors_to_show.unreliable_data_strings as error_string}
                    <div class="vertical-graph__info-error-modal-content">
                        {error_string}
                    </div>
            {/each}
            {/if}
        </div>
    </Modal>
</div>

<style>
    .vertical-graph__graph {
        position: relative;
        height: 448px;
        width: 100%;
    }
    #vertical-graph__scale {
        position: absolute;
        cursor: pointer;
    }
    .vertical-graph__bottom-axis-label {
        text-transform: uppercase;
        font-size: 10.5px;
    }
    .vertical-graph__caution-icon {
        cursor: pointer;
    }
    #vertical-graph__info-error-modal {
        margin: 16px 32px;
    }
    .vertical-graph__info-error-modal-header {
        color: #444;
        font-size: 15px;
        font-weight: 700;
    }
    .vertical-graph__info-error-modal-header:not(:first-of-type){
        padding-top: 16px;
    }
    .vertical-graph__info-error-modal-content {
        color: #777;
        font-size: 16px;
        padding-top: 4px;
    }
    @media screen and (min-width: 1199px) {
        .vertical-graph__graph {
            width: 488px;
        }
    }
    @media screen and (min-height: 1000px) {
        .vertical-graph__graph {
            height: 512px;
        }
    }
</style>